'use client'

import Link from 'next/link'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import type { DashboardTodoItem } from '@/types/dashboard'

interface TodoListProps {
  todos: DashboardTodoItem[]
  maxPreviewItems?: number
  emptyCtaHref?: string
  emptyCtaLabel?: string
}

export function TodoList({
  todos,
  maxPreviewItems = 3,
  emptyCtaHref = '/freelancer/projects',
  emptyCtaLabel = '프로젝트 찾아보기',
}: TodoListProps) {
  if (todos.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 px-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
        <div className="text-center">
          <CheckCircle2 className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-700">지금 할 일이 없어요.</p>
          <p className="text-xs text-gray-500 mt-1">추천 프로젝트를 보러갈까요?</p>
          <Link href={emptyCtaHref} className="mt-3 inline-flex text-xs font-semibold text-blue-600">{emptyCtaLabel} →</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {todos.map((todo) => {
        const count = todo.count ?? 0
        const items = todo.items ?? []
        const preview = items.slice(0, maxPreviewItems)
        const remaining = Math.max(0, items.length - preview.length)

        return (
          <div
            key={todo.key}
            className="rounded-2xl border border-gray-200 bg-white p-4 hover:border-gray-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-4">
              {/* Left */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {/* minimal bullet */}
                  <span className="mt-[1px] inline-block h-2 w-2 rounded-full bg-blue-500" />
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{todo.title}</h3>

                  {count > 0 && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                      {count}
                    </span>
                  )}
                </div>

                {todo.description && <p className="mt-2 pl-4 text-xs text-gray-600">{todo.description}</p>}

                {/* Sub items */}
                {preview.length > 0 && (
                  <div className="mt-2 pl-4 space-y-1">
                    {preview.map((item) => (
                      <Link
                        key={item.id}
                        href={item.href}
                        className="block text-xs text-gray-600 hover:text-gray-900 transition-colors truncate"
                      >
                        • {item.title}
                        {item.meta && <span className="text-gray-400 ml-2">({item.meta})</span>}
                      </Link>
                    ))}

                    {remaining > 0 && (
                      <Link
                        href={todo.href}
                        className="inline-block text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        + {remaining}개 더 보기
                      </Link>
                    )}
                  </div>
                )}
              </div>

              {/* Right CTA */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Mobile: icon button */}
                <Link
                  href={todo.href}
                  className="sm:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                  aria-label="확인하기"
                >
                  <ArrowRight className="h-4 w-4 text-gray-700" />
                </Link>

                {/* Desktop: text button */}
                <Link
                  href={todo.href}
                  className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  확인하기
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
